import {Component, computed, inject, isDevMode, output, signal} from '@angular/core';
import {MatTooltipModule} from '@angular/material/tooltip';
import {CheckboxComponent} from '../../_ui/components/controls/checkbox/checkbox.component';
import {SelectComponent} from '../../_ui/components/controls/select/select.component';
import {GrantHttpService} from '../../_modules/company/access/grant-http.service';
import {
  GrantScopeService, GrantTargetCompany, GrantTargetShard
} from '../../_modules/company/access/grant-scope.service';
import {
  GrantScope,
  SelfGrantResult,
  grantableRoles,
  roleDescription,
  roleLabel,
  selfGrantErrorMessage
} from '../../_modules/company/access/grant.models';
import {GlobalAlertService} from '../../_services/global-alert.service';
import {ViewportService} from '../../_services/viewport.service';
import {AlertLevel} from '../../enums/alert-level';

/**
 * One entry of the *Bereich* picker.
 *
 * `cqme-select` is generic over `TItem extends object`, so the two scopes have to be objects rather than bare
 * enum members. `value` holds the {@link GrantScope} **ordinal**, which is what the backend binds — a string
 * here would compile, render, and POST a scope the server reads as `Company`.
 */
interface ScopeOption {
  value: GrantScope;
  label: string;
}

/**
 * **Self-activation**: a platform owner giving *itself* access to a company or shard, from the context picker.
 *
 * ### Why this belongs in the picker
 *
 * ADR #260 §3a decided that a platform owner reaches a tenant by *selecting a context*, not by borrowing a
 * person. What it did not supply was the row that makes a tenant selectable: `ContextSelection.Resolve`
 * enumerates grants, and a platform owner holds none — so the picker showed them the platform shell, a note
 * admitting the list was not their real list, and no way to act on it. Setting up a new installation therefore
 * still meant impersonating a user who might not exist yet. This panel is the missing step, and it lives beside
 * the tiles because "I have no tile for this tenant" is the exact moment it is needed.
 *
 * ### Why it is deliberately wordy
 *
 * This is a privilege escalation performed by the person who benefits from it, so the panel is built to be
 * *legible* rather than quick:
 *
 *  * every role carries the same sentence the invitation screen shows an administrator before they hand it to
 *    somebody else (`roleDescription`) — the reader of a privilege must not get a shorter description than its
 *    recipient;
 *  * a summary line states, in one sentence, exactly what is about to be granted and where, before the button
 *    can be pressed;
 *  * the reason field is offered because "why" is the one thing the audit trail cannot reconstruct afterwards;
 *  * the success state names the tenant back and says plainly that the act was recorded, rather than quietly
 *    closing.
 *
 * ### What it can never do
 *
 * `GrantIssuancePolicy` refuses `PlatformOwner`, `Register` and `Employee` at every scope, so this cannot widen
 * the platform role itself, mint a device role, or manufacture an employee. {@link offeredRoles} mirrors that
 * list; the server is the authority and answers `E_GRANT_INVITE_ROLE_NOT_GRANTABLE` if the two ever drift, so a
 * drift shows up as a refusal rather than as privilege.
 *
 * It is **not** impersonation and must not drift into it: the session that follows is the operator's own
 * identity narrowed to the roles granted here, with no `impersonated_by` and nobody else's limits.
 * {@link TileImpersonationPanelComponent} is the other half, kept separate exactly as the ADR separated them.
 */
@Component({
  selector: 'auth-self-activation-panel',
  standalone: true,
  host: {class: 'block'},
  imports: [SelectComponent, CheckboxComponent, MatTooltipModule],
  template: `
    <section class="rounded-2xl border-[1px] border-amber-200 bg-amber-50 p-4"
             aria-labelledby="self-activation-heading">
      <h2 id="self-activation-heading" class="text-base font-bold tracking-tight text-gray-900">
        Zugang selbst freischalten
      </h2>
      <!-- gray-600, not -500: the surface is amber-50, where -500 fails AA at this size (style guide). -->
      <p class="mt-1 text-sm text-gray-600">
        Sie erteilen sich selbst Rechte in einer fremden Firma. Der Vorgang wird protokolliert.
      </p>

      @if (created(); as result) {
        <div class="mt-3 rounded-xl border-[1px] border-green-200 bg-green-50 p-3" role="status">
          <p class="text-sm font-bold text-green-800">Zugang freigeschaltet</p>
          <p class="mt-1 text-sm text-gray-700">{{ confirmation(result) }}</p>
          <p class="mt-1 text-sm text-gray-600">
            Der Vorgang steht im Prüfprotokoll unter „RoleGrant / SelfGranted“.
          </p>
          <button type="button" (click)="reset()"
                  class="mt-3 inline-flex items-center justify-center rounded-2xl border-[1px] border-gray-200
                         bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-all
                         duration-200 hover:bg-gray-50 active:scale-95">
            Weiteren Zugang freischalten
          </button>
        </div>
      } @else {
        <div class="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label for="self-company" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
              Firma
            </label>
            <!-- The "?? undefined" is load-bearing: ng-select treats null/'' as a DEFINED value, fails to
                 match it against any item, and synthesises a blank selected entry where the placeholder
                 belongs. Only undefined means "nothing chosen". -->
            <cqme-select id="self-company" size="md" [options]="companies()" [labelKey]="'name'"
                         [valueKey]="'id'" [clearable]="true" placeholder="Firma wählen …"
                         ariaLabel="Firma, in der Sie sich freischalten"
                         [current]="companyId() || undefined"
                         (currentChange)="chooseCompany(one($event))"/>
            @if (!companies().length && !loadingCompanies()) {
              <p class="mt-2 text-sm font-semibold text-red-700" role="alert">
                Die Firmenliste konnte nicht geladen werden.
                <button type="button" (click)="loadCompanies()" class="underline">Erneut versuchen</button>
              </p>
            }
          </div>

          <div>
            <label for="self-scope" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
              Bereich
            </label>
            <!-- Not clearable: "no scope" is not a state the request can carry. -->
            <cqme-select id="self-scope" size="md" [options]="scopeOptions" [labelKey]="'label'"
                         [valueKey]="'value'" ariaLabel="Bereich des Zugangs"
                         [current]="scope()" (currentChange)="setScope($event)"/>
          </div>

          @if (scope() === Scope.Shard) {
            <div class="md:col-span-2">
              <label for="self-shard" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
                Standort
              </label>
              <cqme-select id="self-shard" size="md" [options]="shards()" [labelKey]="'name'"
                           [valueKey]="'id'" [clearable]="true" placeholder="Standort wählen …"
                           ariaLabel="Standort des Zugangs"
                           [current]="shardId() || undefined"
                           (currentChange)="shardId.set(one($event))"/>
              @if (companyId() && !shards().length) {
                <p class="mt-2 text-sm text-gray-600">Für diese Firma sind keine Standorte hinterlegt.</p>
              }
            </div>
          }
        </div>

        <fieldset class="mt-4">
          <legend class="mb-2 block text-sm font-bold tracking-tight text-gray-700">Rollen</legend>
          <div class="flex flex-col gap-y-3 md:flex-row md:flex-wrap md:gap-x-6 md:gap-y-3">
            @for (role of offeredRoles(); track role) {
              <!-- The tooltip sits on the wrapper rather than on cqme-checkbox: Material's hover/focus
                   monitoring covers descendants, so the hint opens without giving a shared design-system
                   control an input two other screens do not want. -->
              <div class="md:max-w-xs" [class.cursor-help]="!compact() && !!description(role)"
                   [matTooltip]="description(role)" [matTooltipDisabled]="compact()"
                   matTooltipClass="prose-tooltip" matTooltipPosition="above">
                <cqme-checkbox [id]="'self-role-' + role" [label]="label(role)"
                               [checked]="roles().includes(role)" (checkedChange)="toggleRole(role)"/>
                @if (compact() && description(role)) {
                  <p class="ml-7 mt-0.5 text-xs text-gray-600">{{ description(role) }}</p>
                }
              </div>
            }
          </div>
        </fieldset>

        <div class="mt-4">
          <label for="self-duration" class="mb-2 flex items-baseline justify-between text-sm font-bold
                                             tracking-tight text-gray-700">
            <span>Dauer</span>
            <span class="font-semibold text-theme-primary-700" data-test="self-duration-label">
              {{ durationLabel() }}
            </span>
          </label>
          <!-- A styled native range, not a design-system control: the UI kit (_ui/components/controls) has no
               slider today. Keyboard-operable and labelled out of the box (arrow keys step by 1, matching the
               hourly granularity), so no extra wiring is needed to keep it accessible. -->
          <input id="self-duration" type="range" min="1" max="8" step="1" [value]="durationHours()"
                 (input)="durationHours.set(intValue($event))" aria-label="Dauer des Zugangs"
                 [attr.aria-valuetext]="durationLabel()"
                 class="h-2 w-full cursor-pointer appearance-none rounded-full bg-gray-200 accent-theme-primary-600
                        focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-primary-500
                        focus-visible:ring-offset-2">
          <p class="mt-1 text-sm text-gray-600">Der Zugang läuft danach automatisch ab.</p>
        </div>

        <div class="mt-4">
          <label for="self-reason" class="mb-2 block text-sm font-bold tracking-tight text-gray-700">
            Grund (optional)
          </label>
          <input id="self-reason" type="text" [value]="reason()" (input)="reason.set(value($event))"
                 placeholder="z. B. Ersteinrichtung der Kasse"
                 class="block w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm shadow-sm
                        transition-all duration-200 placeholder-gray-300 focus:border-theme-primary-500
                        focus:ring-1 focus:ring-theme-primary-500">
          <p class="mt-1 text-sm text-gray-600">Der Grund steht später im Prüfprotokoll.</p>
        </div>

        <!-- The whole point of the panel: say what is about to happen, in one sentence, before it happens. -->
        @if (summary(); as sentence) {
          <p class="mt-4 rounded-xl border-[1px] border-amber-300 bg-white px-4 py-3 text-sm font-semibold
                    text-gray-800" data-test="self-activation-summary">
            {{ sentence }}
          </p>
        }

        <div class="mt-4 flex items-center justify-end gap-3">
          @if (isDev) {
            <!-- Development only (never in a prod build): issue the grant for 1 minute to watch it expire. -->
            <button type="button" (click)="submit(2)" [disabled]="!canSubmit()"
                    title="Nur in der Entwicklung: Zugang für 2 Minuten, um Ablauf-Warnung und automatischen Ablauf zu testen."
                    class="inline-flex items-center justify-center rounded-2xl border-[1px] border-amber-300
                           bg-white px-4 py-2.5 text-sm font-semibold text-amber-700 shadow-sm transition-all
                           duration-200 hover:bg-amber-50 active:scale-95 disabled:cursor-not-allowed
                           disabled:opacity-30">
              2 Min (Debug)
            </button>
          }
          <button type="button" (click)="submit()" [disabled]="!canSubmit()"
                  class="inline-flex items-center justify-center rounded-2xl bg-theme-primary-600 px-5 py-2.5
                         text-sm font-semibold text-white shadow-lg shadow-theme-primary-100 transition-all
                         duration-200 hover:bg-theme-primary-700 hover:shadow-xl active:scale-95
                         disabled:cursor-not-allowed disabled:opacity-30 disabled:grayscale">
            Zugang freischalten
          </button>
        </div>
      }
    </section>
  `
})
export class SelfActivationPanelComponent {
  private readonly grants = inject(GrantHttpService);
  private readonly scopes$ = inject(GrantScopeService);
  private readonly alerts = inject(GlobalAlertService);

  /**
   * Emitted once a grant actually exists, so the surrounding picker can reload its tiles.
   *
   * Deliberately fired on **success only** and carrying nothing: the panel already renders the confirmation, and
   * a parent that re-fetched on refusals would flicker its list for a request that changed nothing.
   */
  readonly granted = output<void>();

  protected readonly Scope = GrantScope;

  /** The two entries of the scope picker. A field, not a template literal, because `[valueKey]` needs one. */
  protected readonly scopeOptions: ScopeOption[] = [
    {value: GrantScope.Company, label: 'Gesamte Firma'},
    {value: GrantScope.Shard, label: 'Einzelner Standort'}
  ];

  protected readonly companies = signal<GrantTargetCompany[]>([]);
  protected readonly loadingCompanies = signal(false);
  protected readonly shards = signal<GrantTargetShard[]>([]);

  protected readonly companyId = signal('');
  protected readonly scope = signal<GrantScope>(GrantScope.Shard);
  protected readonly shardId = signal('');
  protected readonly roles = signal<string[]>([]);
  protected readonly reason = signal('');

  /** The duration slider's value, in whole hours — `1`–`8`, default `1`. Sent as `DurationHours`. */
  protected readonly durationHours = signal(1);

  /** True under `ng serve` only; gates the dev-only sub-hour "Debug" button. Always false in a prod build. */
  protected readonly isDev = isDevMode();

  /** The live label beside the slider: "1 Stunde" at the minimum, "X Stunden" everywhere else. */
  protected readonly durationLabel = computed(() =>
    this.durationHours() === 1 ? '1 Stunde' : `${this.durationHours()} Stunden`);

  protected readonly busy = signal(false);
  protected readonly created = signal<SelfGrantResult | null>(null);

  protected readonly offeredRoles = computed(() => grantableRoles(this.scope()));

  protected readonly label = roleLabel;
  protected readonly description = roleDescription;

  /**
   * Which side of `md` the window is on, and therefore which of the two description mechanisms is live —
   * below it the inline text is rendered and `matTooltipDisabled` stops Material describing the wrapper; from
   * `md` up the text is not in the DOM and the tooltip's `aria-describedby` is the only description. One source
   * for both, so exactly one copy reaches assistive technology at every width. The reasoning is written out
   * once, on `AccessManagementComponent`.
   */
  protected readonly compact = inject(ViewportService).isCompact;

  /**
   * Whether the form is complete enough to send. Mirrors the server's own preconditions, so the common mistake
   * is a disabled button rather than a refusal.
   */
  protected readonly canSubmit = computed(() =>
    !this.busy()
    && !!this.companyId()
    && this.roles().length > 0
    && (this.scope() !== GrantScope.Shard || !!this.shardId()));

  /**
   * The sentence shown above the button: what is about to be granted, where.
   *
   * Null until there is something true to say — an incomplete form gets no summary rather than a half one,
   * because a sentence naming a company but no role would read as a smaller grant than the one being prepared.
   */
  protected readonly summary = computed(() => {
    if (!this.companyId() || !this.roles().length) return null;
    if (this.scope() === GrantScope.Shard && !this.shardId()) return null;
    return `Sie erteilen sich ${this.roleList(this.roles())} für ${this.where()}, gültig für `
      + `${this.durationLabel()}.`;
  });

  constructor() {
    this.loadCompanies();
  }

  protected loadCompanies(): void {
    this.loadingCompanies.set(true);
    this.scopes$.listCompanies().subscribe(companies => {
      this.loadingCompanies.set(false);
      this.companies.set(companies);
    });
  }

  protected chooseCompany(id: string): void {
    this.companyId.set(id);
    // The previously chosen shard belonged to the previous company. Keeping it would let the form POST a shard
    // the server refuses as "not this company's" — a refusal the operator cannot see coming.
    this.shardId.set('');
    this.shards.set([]);
    if (!id) return;
    this.scopes$.listShards(id).subscribe(shards => this.shards.set(shards));
  }

  protected setScope(value: GrantScope | GrantScope[] | undefined): void {
    const scope = Array.isArray(value) ? value[0] : value;
    if (scope === undefined) return;
    this.scope.set(scope);
    // The role vocabulary differs between the scopes, so a role chosen at the old one may not be grantable at
    // the new one. Dropping the ones that are no longer offered beats sending a request the server refuses
    // wholesale for a role the operator can no longer even see.
    const offered = grantableRoles(scope);
    this.roles.update(current => current.filter(role => offered.includes(role)));
    if (scope === GrantScope.Company) this.shardId.set('');
  }

  protected toggleRole(role: string): void {
    this.roles.update(current =>
      current.includes(role) ? current.filter(r => r !== role) : [...current, role]);
  }

  protected submit(debugMinutes?: number): void {
    if (!this.canSubmit()) return;
    this.busy.set(true);

    this.grants.grantSelf({
      Scope: this.scope(),
      CompanyId: this.companyId(),
      ShardId: this.scope() === GrantScope.Shard ? this.shardId() : null,
      Roles: this.roles(),
      Reason: this.reason().trim() || null,
      DurationHours: this.durationHours(),
      // Dev-only: when the "2 Min (Debug)" button is used, ask for a sub-hour lifetime. Spread so the key is
      // absent (not undefined) on the normal path; the server ignores it outside Development regardless.
      ...(debugMinutes !== undefined ? {DebugDurationMinutes: debugMinutes} : {})
    }).subscribe({
      next: result => {
        this.busy.set(false);
        this.created.set(result);
        this.granted.emit();
      },
      error: error => {
        this.busy.set(false);
        // German, mapped from the CODE. `description` is the server's developer-facing English and must never
        // be rendered (style guide, "Surfacing an API error"). The fallback is a real code path here: an
        // unknown company or shard answers a bare 404 carrying no ApiError at all.
        this.alerts.createAndShow('Freischaltung nicht möglich',
          selfGrantErrorMessage(error, 'Der Zugang konnte nicht freigeschaltet werden. Bitte versuchen Sie es '
            + 'erneut.'),
          AlertLevel.error, 8000);
      }
    });
  }

  /** Clears the form back to a fresh one after a success, keeping the chosen company for a second location. */
  protected reset(): void {
    this.created.set(null);
    this.roles.set([]);
    this.reason.set('');
    this.shardId.set('');
    this.durationHours.set(1);
  }

  /**
   * The confirmation sentence, built from what the SERVER said it created rather than from the form —
   * including the expiry it stamped, so the operator is told exactly how long the elevation lasts rather than
   * having to remember the slider's last position. `GrantSelf` clamps `DurationHours` server-side, so
   * `result.expiresAt` — not the form's own {@link durationHours} — is the one true answer to "until when".
   */
  protected confirmation(result: SelfGrantResult): string {
    const roles = this.roleList(result.granted.map(g => g.role));
    const company = result.companyName || result.companyId;
    const where = result.scope === GrantScope.Shard
      ? `${result.shardName || result.shardId} (${company})`
      : `${company} — gesamte Firma`;
    const expiry = result.expiresAt ? ` Der Zugang läuft am ${this.formatExpiry(result.expiresAt)} automatisch ab.`
      : '';
    return `Sie halten jetzt ${roles} für ${where}.${expiry}`;
  }

  /** "TT.MM.JJJJ, HH:MM Uhr" — the date is included because an 8-hour grant can run past midnight. */
  private formatExpiry(iso: string): string {
    const formatted = new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
    return `${formatted} Uhr`;
  }

  /** Where the pending grant applies, in the form's own current terms. */
  private where(): string {
    const company = this.companies().find(c => c.id === this.companyId())?.name || this.companyId();
    if (this.scope() === GrantScope.Company) return `${company} — gesamte Firma`;
    const shard = this.shards().find(s => s.id === this.shardId())?.name || this.shardId();
    return `${shard} (${company})`;
  }

  /** German role names, joined — the one line every "what is being granted" sentence renders a role set as. */
  private roleList(roles: readonly string[]): string {
    return roles.map(roleLabel).join(', ');
  }

  /**
   * Narrows what a single-value `cqme-select` emits down to the one id these pickers store. The control's
   * output type carries an array because the same component also does multi-select; neither picker here is
   * `[multiple]`, so only `undefined` (a clear) can actually arrive beside a string.
   */
  protected one(value: string | string[] | undefined): string {
    return (Array.isArray(value) ? value[0] : value) ?? '';
  }

  protected value(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  /** As {@link value}, but for the duration slider — a range input's value is always a numeric string. */
  protected intValue(event: Event): number {
    return Number((event.target as HTMLInputElement).value);
  }
}
